import { useQueryClient } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { medicationApi } from 'src/api/medication';
import { invalidateDosingSuggestions } from 'src/sections/@dashboard/profile/components/medication-form/medicationDosingSuggestion';
import type { MedicationsFormModalValuesProps } from 'src/sections/@dashboard/profile/components/medication-form/medicationFormSchema';
import {
  ClinicalWriteRequest,
  ClinicalWriteRoute,
  ClinicalWriteSeries,
  useClinicalWriteSeries,
} from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import {
  AddFacilityMedicationDto,
  UpdateFacilityMedicationDto,
} from 'woofwoof-api';
import { buildMedicationSubmitPayload } from '../utils/buildMedicationSubmitPayload';

/**
 * Every Facility medication create and edit is a series of this kind, on
 * every screen, so a form reopened anywhere resends what another one froze.
 */
export const MEDICATION_WRITE = 'clinical-medication';

export type MedicationCreateSeries = ClinicalWriteSeries<
  null,
  AddFacilityMedicationDto,
  MedicationsFormModalValuesProps
>;
export type MedicationEditSeries = ClinicalWriteSeries<
  string,
  UpdateFacilityMedicationDto,
  MedicationsFormModalValuesProps
>;
type CreateRequest = ClinicalWriteRequest<
  null,
  AddFacilityMedicationDto,
  MedicationsFormModalValuesProps
>;
type EditRequest = ClinicalWriteRequest<
  string,
  UpdateFacilityMedicationDto,
  MedicationsFormModalValuesProps
>;

/** R4-3: the Facility body (dosing, stock and the reminder intent) a series freezes. */
export function facilityMedicationBody(
  values: MedicationsFormModalValuesProps,
): AddFacilityMedicationDto & UpdateFacilityMedicationDto {
  return buildMedicationSubmitPayload(values) as AddFacilityMedicationDto &
    UpdateFacilityMedicationDto;
}

/*
 * The endpoint is read from the frozen route's `via` alone: the patient's
 * route (with the visit as its context), or the visit's own route, where the
 * server resolves the patient (none on a guest visit). The backend hashes the
 * route into the operation key, so a resend from another screen must go
 * exactly where the first attempt went.
 */
function sendCreate(
  { route, body }: CreateRequest,
  options: AxiosRequestConfig,
): Promise<unknown> {
  return route.via === 'patient'
    ? medicationApi.medicationControllerAddProfileMedicationInOrganization(
        {
          profileId: route.profileId,
          addFacilityMedicationDto: {
            ...body,
            visitId: route.visitId ?? undefined,
          },
        },
        options,
      )
    : medicationApi.medicationControllerAddVisitMedication(
        { visitId: route.visitId, addFacilityMedicationDto: body },
        options,
      );
}

function sendEdit(
  { route, target: medicationId, body }: EditRequest,
  options: AxiosRequestConfig,
): Promise<unknown> {
  return route.via === 'patient'
    ? medicationApi.medicationControllerUpdateProfileMedicationInOrganization(
        {
          profileId: route.profileId,
          medicationId,
          updateFacilityMedicationDto: {
            ...body,
            visitId: route.visitId ?? undefined,
          },
        },
        options,
      )
    : medicationApi.medicationControllerUpdateVisitMedication(
        {
          visitId: route.visitId,
          medicationId,
          updateFacilityMedicationDto: body,
        },
        options,
      );
}

interface MedicationWriteOptions {
  /**
   * The screen's patient, visit and endpoint (`via`); a create's series is
   * the patient's and visit's, whichever endpoint (see `ClinicalWriteRoute`).
   */
  route: ClinicalWriteRoute;
  /** After the Warehouse refresh and shortage snackbars. */
  onSuccess: (response: unknown) => void;
  onError: (error: unknown) => void;
}

/**
 * R3-4: a Facility medication create, with or without stock, as a clinical
 * write series keyed by its patient and visit (notes-2, Tasks 22/24; fix 22),
 * so every screen of one patient and visit shares it. A success refreshes the
 * Warehouse and reports each shortage first, and refreshes the clinic's
 * dosing history the dosage autofill reads (#692).
 */
export function useMedicationCreateSeries({
  route,
  onSuccess,
  onError,
}: MedicationWriteOptions): MedicationCreateSeries {
  const onStockWrite = useStockWriteFeedback();
  const queryClient = useQueryClient();
  return useClinicalWriteSeries(sendCreate, {
    operation: MEDICATION_WRITE,
    target: null,
    route,
    onSuccess: (response, request) => {
      onStockWrite(response);
      invalidateDosingSuggestions(queryClient, request.organizationId);
      onSuccess(response);
    },
    onError,
  });
}

/**
 * R3-4: a Facility medication edit as a clinical write series of the entry
 * `editingId` (`''` while no medication is open): `pending` and the frozen
 * values are that entry's. A success refreshes what a create's does.
 */
export function useMedicationEditSeries({
  route,
  editingId,
  onSuccess,
  onError,
}: MedicationWriteOptions & { editingId: string }): MedicationEditSeries {
  const onStockWrite = useStockWriteFeedback();
  const queryClient = useQueryClient();
  return useClinicalWriteSeries(sendEdit, {
    operation: MEDICATION_WRITE,
    target: editingId,
    route,
    onSuccess: (response, request) => {
      onStockWrite(response);
      invalidateDosingSuggestions(queryClient, request.organizationId);
      onSuccess(response);
    },
    onError,
  });
}
